import firebaseConfig, { vapidKey } from "../constants/firebaseConfig";
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  sendEmailVerification,
  updateEmail as updateEmailFirebase,
  updatePassword as firebaseUpdatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  // PhoneAuthProvider,
  linkWithCredential,
  linkWithPhoneNumber,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  deleteUser,
  // onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  // updatePassword,
  // updateProfile
} from "firebase/auth";
import { JSHmac, CONSTANTS } from "react-hash";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { getAnalytics } from "firebase/analytics";
// import { RegisterWithGoogleResponse, RegisterWithEmailAndPasswordResponse } from "../types/FirebaseRequestTypes";

import {
  getMessaging,
  getToken,
  onMessage,
  deleteToken
} from 'firebase/messaging'
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import { addNotification, setNotificationInitializer, setNotificationToken } from "../store/slices/appSlice";
import { reportClientAuthError } from "../utils/clientAuthErrorReport";
import { parseFcmPayloadToNavItem, isMessageRelatedNotification } from "../utils/notificationHelpers";
import { apiSlice } from "../store/api/apiSlice";

let firebasePersistenceInitialized = false;

const useFirebase = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch()
  const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  const auth = getAuth(app);
  auth.languageCode = 'tr';
  // Firebase'te email verification zorunlu hale getir (sadece bir kez ayarla)
  if (!firebasePersistenceInitialized) {
    setPersistence(auth, browserLocalPersistence).catch((err) => {
      console.log('[useFirebase] Persistence ayarlanamadı:', err);
    });
    firebasePersistenceInitialized = true;
  }
  const messaging = getMessaging(app);
  const secret = process.env.REACT_APP_APP_SECRET;
  const provider = new GoogleAuthProvider();
  // Firebase Analytics'i sadece production'da çalıştır
  const analytics = process.env.NODE_ENV === 'production' ? getAnalytics(app) : null;

  const handleIncomingFcm = (payload) => {
    const item = parseFcmPayloadToNavItem(payload);
    dispatch(addNotification(item));
    const tags = [{ type: "Notification", id: "LIST" }];
    // Mesaj bildirimi → unread rozeti API'den yenilensin (FCM sayısı rozete eklenmez)
    if (isMessageRelatedNotification(item)) {
      tags.push("MessageRoomUnread", "MessageRoomUnreadTotal");
    }
    dispatch(apiSlice.util.invalidateTags(tags));
    return item;
  };

  //!auth
  const register = async (email, password) => {
    try {
      const { user } = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      try {
        const idToken = await user.getIdToken();
        // eslint-disable-next-line no-param-reassign
        user.accessToken = idToken;
      } catch (_) { }
      return user;
    } catch (err) {
      console.log('💥 [useFirebase] Firebase hatası:', err);
      reportClientAuthError({
        provider: "EmailPassword",
        stage: "createUserWithEmailAndPassword",
        identifier: email ? String(email).toLowerCase().trim() : null,
        error: err,
      });
      return null;
    }
  };

  const firebaseLogin = async (email, password) => {
    const identifier = email ? String(email).toLowerCase().trim() : null;
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      // Email verification kontrolünü kaldırdık - backend'den kontrol edilecek
      try {
        const idToken = await user.getIdToken();
        // eslint-disable-next-line no-param-reassign
        user.accessToken = idToken;
      } catch (tokenErr) {
        reportClientAuthError({
          provider: "EmailPassword",
          stage: "getIdTokenAfterSignIn",
          identifier,
          error: tokenErr,
        });
      }
      return user;
    } catch (err) {
      console.log('💥 [useFirebase] Firebase hatası:', err);
      reportClientAuthError({
        provider: "EmailPassword",
        stage: "signInWithEmailAndPassword",
        identifier,
        error: err,
      });
      return err;
    }
  };

  const logout = async () => {
    await signOut(auth);
    return true;
  };

  const updateEmail = async (email) => {
    try {
      await updateEmailFirebase(auth.currentUser, email);
      return true;
    } catch (err) {
      console.log('💥 [useFirebase] Firebase hatası:', err);
      return false;
    }
  };

  const getSignature = async (token) => {
    const hashedData = await JSHmac(
      token,
      secret,
      CONSTANTS.HmacAlgorithms.HmacSHA512
    );
    return hashedData;
  };

  const updatePassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success(t("password_reset_email_sent"));
      return {
        isSuccess: true,
        message: t("password_reset_email_sent")
      };
    } catch (err) {
      console.log('[useFirebase] Firebase şifre sıfırlama hatası:', err);
      reportClientAuthError({
        provider: "EmailPassword",
        stage: "sendPasswordResetEmail",
        identifier: email ? String(email).toLowerCase().trim() : null,
        error: err,
      });
      toast.error(t("password_reset_email_failed"));
      return {
        isSuccess: false,
        errorCode: err.code,
        errorMessage: err.message
      };
    }
  };

  /** Firebase password provider bağlı mı? (email+şifre ile giriş mümkün mü) */
  const hasPasswordProvider = (user = auth.currentUser) =>
    Boolean((user?.providerData || []).some((p) => p.providerId === "password"));

  /**
   * Oturumdayken şifre değiştir (doküman önerilen yol):
   * reauthenticateWithCredential + updatePassword — eski şifre Firebase'de doğrulanır.
   */
  const changePasswordWithReauth = async (currentPassword, newPassword) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return {
          isSuccess: false,
          errorCode: "no-current-user",
          errorMessage: "Mevcut Firebase oturumu bulunamadı",
        };
      }
      if (!hasPasswordProvider(currentUser)) {
        return {
          isSuccess: false,
          errorCode: "no-password-provider",
          errorMessage: "Password provider yok; Password/Set kullanılmalı",
        };
      }
      const email = currentUser.email;
      if (!email) {
        return {
          isSuccess: false,
          errorCode: "no-email",
          errorMessage: "Kullanıcı e-postası bulunamadı",
        };
      }
      const credential = EmailAuthProvider.credential(email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await firebaseUpdatePassword(currentUser, newPassword);
      return { isSuccess: true, user: currentUser };
    } catch (err) {
      reportClientAuthError({
        provider: "EmailPassword",
        stage: "changePasswordWithReauth",
        identifier: auth.currentUser?.email
          ? String(auth.currentUser.email).toLowerCase().trim()
          : null,
        error: err,
      });
      return {
        isSuccess: false,
        errorCode: err.code,
        errorMessage: err.message,
      };
    }
  };

  const signInWithGoogle = async () => {
    return signInWithPopup(auth, provider)
      .then((result) => {
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const token = credential?.accessToken || null;
        const user = result.user;
        // Firebase ID token'ı kullanıcıya ekle
        // Bu, kodun geri kalanında user.accessToken beklentisini karşılar
        return user.getIdToken().then((idToken) => {
          // eslint-disable-next-line no-param-reassign
          user.accessToken = idToken;
          return {
            isSuccess: true,
            credential,
            token,
            user,
          }
        });
      })
      .catch((err) => {
        const errorCode = err.code;
        const errorMessage = err.message;
        const email = err.customData?.email;
        const credential = GoogleAuthProvider.credentialFromError(err);
        console.log('[useFirebase] Bu hataları işle dostum!!!')
        reportClientAuthError({
          provider: "Google",
          stage: "signInWithGoogle",
          identifier: err?.customData?.email || null,
          errorCode: err?.code || null,
          errorMessage: err?.message || "Google sign in failed"
        });
        return {
          isSuccess: false,
          errorCode,
          errorMessage,
          credential,
          email,
        }
      })
  }

  const generateRecaptcha = (elementOrId) => {
    try {
      // Element kontrolü
      if (!elementOrId) {
        return null;
      }

      // String ID ise DOM'dan element'i bul
      let element = elementOrId;
      if (typeof elementOrId === 'string') {
        element = document.getElementById(elementOrId);
        if (!element) {
          return null;
        }
      }

      // Element'in DOM'da olduğundan emin ol
      if (!element.parentNode && element !== document.body) {
        return null;
      }

      // Element'in hala geçerli olduğunu kontrol et
      if (!element || typeof element.style === 'undefined') {
        return null;
      }

      // Eğer aynı element için zaten render edilmişse tekrar render etme
      try {
        if (window.recaptchaVerifier && window.recaptchaContainer === element) {
          // Container hala DOM'da mı kontrol et
          if (element.parentNode || element === document.body) {
            return window.recaptchaVerifier;
          } else {
            // Container DOM'dan kaldırılmış, temizle
            try {
              if (window.recaptchaVerifier.clear) {
                window.recaptchaVerifier.clear();
              }
            } catch (e) {
              // Silent fail
            }
            try { delete window.recaptchaVerifier; } catch (_) { }
            try { delete window.recaptchaContainer; } catch (_) { }
          }
        }
        // Farklı container için varsa temizle
        if (window.recaptchaVerifier) {
          try {
            window.recaptchaVerifier.clear();
          } catch (e) {
            // Silent fail
          }
          try { delete window.recaptchaVerifier; } catch (_) { }
          try { delete window.recaptchaContainer; } catch (_) { }
        }
      } catch (e) {
        // Silent fail
      }

      // Element'in hala DOM'da olduğunu tekrar kontrol et (race condition için)
      if (!element.parentNode && element !== document.body) {
        return null;
      }
      if (!element || typeof element.style === 'undefined') {
        return null;
      }

      // Yeni reCAPTCHA oluştur
      let appVerifier;
      try {
        appVerifier = new RecaptchaVerifier(auth, element, {
          'size': 'invisible',
          'callback': () => {
            // Callback handled silently
          },
          'expired-callback': () => {
            // Expired handled silently
          },
          'error-callback': (error) => {
            reportClientAuthError({
              provider: "FirebasePhone",
              stage: "captcha",
              errorCode: error?.code || null,
              errorMessage: error?.message || "reCAPTCHA callback error"
            });
            // Error callback'te element null olabilir, kontrol et
            try {
              if (window.recaptchaVerifier && window.recaptchaContainer) {
                const container = window.recaptchaContainer;
                if (!container || !container.parentNode || typeof container.style === 'undefined') {
                  try {
                    if (window.recaptchaVerifier && window.recaptchaVerifier.clear) {
                      window.recaptchaVerifier.clear();
                    }
                  } catch (e) {
                    // Silent fail
                  }
                  try { delete window.recaptchaVerifier; } catch (_) { }
                  try { delete window.recaptchaContainer; } catch (_) { }
                }
              }
            } catch (e) {
              // Silent fail
            }
          }
        });
      } catch (error) {
        // RecaptchaVerifier oluşturma hatası
        console.error("RecaptchaVerifier oluşturma hatası:", error);
        reportClientAuthError({
          provider: "FirebasePhone",
          stage: "captcha",
          errorCode: error?.code || null,
          errorMessage: error?.message || "RecaptchaVerifier create failed"
        });
        return null;
      }

      // Global olarak sakla
      window.recaptchaVerifier = appVerifier;
      window.recaptchaContainer = element;

      return appVerifier;
    } catch (error) {
      console.error("reCAPTCHA oluşturma hatası:", error);
      reportClientAuthError({
        provider: "FirebasePhone",
        stage: "captcha",
        errorCode: error?.code || null,
        errorMessage: error?.message || "reCAPTCHA create failed"
      });
      return null;
    }
  }

  const signInWithPhoneNumberFirebase = async (phoneNumber, appVerifier, setDialog) => {
    try {
      if (!appVerifier) {
        throw new Error("App Verifier bulunamadı");
      }

      // Timeout ile sarmalayalım
      let timeoutId;
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('reCAPTCHA timeout - lütfen tekrar deneyin'));
        }, 30000); // 30 saniye
      });

      // Wrap signInPromise to catch any errors including string "Timeout"
      const signInPromise = signInWithPhoneNumber(auth, phoneNumber, appVerifier).catch((error) => {
        // If error is "Timeout" string, convert to Error object
        if (error === 'Timeout' || error?.toString() === 'Timeout') {
          throw new Error('reCAPTCHA timeout - lütfen tekrar deneyin');
        }
        throw error;
      });

      let confirmationResult;
      try {
        confirmationResult = await Promise.race([signInPromise, timeoutPromise]);
        // Başarılı olduysa timeout'u temizle
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      } catch (raceError) {
        // Timeout hatasını yakala ve daha açıklayıcı bir hata fırlat
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        // Check for timeout in various formats
        const isTimeout = raceError?.message?.toLowerCase().includes('timeout') || 
                         raceError?.message === 'Timeout' ||
                         raceError === 'Timeout' ||
                         raceError?.toString() === 'Timeout';
        if (isTimeout) {
          const timeoutError = new Error('reCAPTCHA doğrulaması zaman aşımına uğradı. Lütfen sayfayı yenileyip tekrar deneyin.');
          throw timeoutError;
        }
        throw raceError;
      }

      // Global olarak sakla
      window.confirmationResult = confirmationResult;

      // setDialog callback'i varsa çağır
      if (typeof setDialog === 'function') {
        setDialog(true);
      }

      return confirmationResult;
    } catch (error) {
      const isCaptchaTimeout = error?.message?.toLowerCase().includes('timeout') || error?.message === 'Timeout';
      reportClientAuthError({
        provider: "FirebasePhone",
        stage: isCaptchaTimeout ? "captcha" : "sendCode",
        identifier: phoneNumber,
        errorCode: error?.code || null,
        errorMessage: error?.message || "Phone code send failed"
      });
      // Timeout hatası için özel mesaj - case insensitive kontrol
      if (error?.message?.toLowerCase().includes('timeout') || error?.message === 'Timeout') {
        throw new Error('reCAPTCHA doğrulaması zaman aşımına uğradı. Lütfen sayfayı yenileyip tekrar deneyin.');
      }

      // Firebase billing-not-enabled hatası için özel işleme
      if (error?.code === 'auth/billing-not-enabled' ||
        error?.code === 'auth/user-not-found' ||
        error?.message?.includes('billing-not-enabled')) {
        // Hata mesajını koru ama kullanıcıya "Kullanıcı bulunamadı" gösterilecek
        const customError = new Error('Kullanıcı bulunamadı');
        customError.code = error.code;
        customError.originalError = error;
        throw customError;
      }

      throw error;
    }
  }

  const registerWithEmailAndPasswordFirebase = async (email, password) => {
    try {
      const res = await createUserWithEmailAndPassword(auth, email, password);
      try {
        const idToken = await res.user.getIdToken();
        // eslint-disable-next-line no-param-reassign
        res.user.accessToken = idToken;
      } catch (_) { }
      return {
        user: res.user,
        isSuccess: true
      }
    } catch (err) {
      return {
        isSuccess: false,
        errorCode: 400,
        errorMessage: err.code
      }
    }
  }

  // Mevcut oturumdaki kullanıcıya email+password kimliğini linkler (tek UID)
  const linkEmailPasswordToCurrentUser = async (email, password) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return { isSuccess: false, errorCode: 'no-current-user', errorMessage: 'Mevcut kullanıcı bulunamadı' };
      }

      // Eğer password provider zaten linkliyse yeniden linklemeye çalışma
      const alreadyHasPasswordProvider = (currentUser.providerData || []).some(p => p.providerId === 'password');
      if (alreadyHasPasswordProvider) {
        try {
          if (currentUser.email !== email) {
            await updateEmailFirebase(currentUser, email);
          }
        } catch (e) {
          // email güncellenemese bile devam edelim (ör. aynı email olabilir)
        }
        try {
          // Şifreyi belirle/güncelle
          await firebaseUpdatePassword(currentUser, password);
        } catch (e) {
          // Bazı durumlarda (policy) şifre güncellenemeyebilir; bu durumda da devam edebiliriz
        }
        const idToken = await currentUser.getIdToken(true);
        return { isSuccess: true, user: currentUser, idToken };
      }

      const credential = EmailAuthProvider.credential(email, password);

      const userCred = await linkWithCredential(currentUser, credential);
      // Email verification zorunlu - yeni linklenen email için verification gönder
      if (!userCred.user.emailVerified) {
        await sendEmailVerification(userCred.user);
      }
      const idToken = await userCred.user.getIdToken(true);
      return { isSuccess: true, user: userCred.user, idToken };
    } catch (err) {
      if (err?.code === 'auth/provider-already-linked') {
        // Sağlamlık: sağlayıcı zaten linkli ise başarı say ve devam et
        const currentUser = auth.currentUser;
        // Email verification kontrolü
        if (currentUser && !currentUser.emailVerified) {
          await sendEmailVerification(currentUser);
        }
        const idToken = currentUser ? await currentUser.getIdToken(true) : '';
        return { isSuccess: true, user: currentUser, idToken };
      }
      return { isSuccess: false, errorCode: err.code, errorMessage: err.message };
    }
  }

  // Mevcut kullanıcıya telefon numarasını linklemek için OTP başlatır
  const linkPhoneNumberToCurrentUser = async (phoneNumber, appVerifier) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return { isSuccess: false, errorCode: 'no-current-user', errorMessage: 'Mevcut kullanıcı bulunamadı' };
      }
      if (!appVerifier) {
        return { isSuccess: false, errorCode: 'no-app-verifier', errorMessage: 'reCAPTCHA doğrulayıcı bulunamadı' };
      }
      const confirmationResult = await linkWithPhoneNumber(currentUser, phoneNumber, appVerifier);
      return { isSuccess: true, confirmationResult };
    } catch (err) {
      return { isSuccess: false, errorCode: err.code, errorMessage: err.message };
    }
  }

  // Telefon linkleme OTP kodunu onaylar
  const confirmLinkedPhoneCode = async (confirmationResult, code) => {
    try {
      const userCred = await confirmationResult.confirm(code);
      const idToken = await userCred.user.getIdToken(true);
      return { isSuccess: true, user: userCred.user, idToken };
    } catch (err) {
      return { isSuccess: false, errorCode: err.code, errorMessage: err.message };
    }
  }

  const deleteUserFirebase = async () => {
    const user = auth.currentUser;
    return deleteUser(user).then(() => {
      return true;
    }).catch(error => {
      return false;
    })
  }

  const sendResetPasswordEmail = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return {
        isSuccess: true,
        message: "Şifre sıfırlama e-postası başarıyla gönderildi"
      };
    } catch (err) {
      console.log('[useFirebase] Firebase şifre sıfırlama hatası:', err);
      return {
        isSuccess: false,
        errorCode: err.code,
        errorMessage: err.message
      };
    }
  }

  const verifyPasswordResetOobCode = async (oobCode) => {
    try {
      const email = await verifyPasswordResetCode(auth, oobCode);
      return { isSuccess: true, email };
    } catch (err) {
      return { isSuccess: false, errorCode: err.code, errorMessage: err.message };
    }
  }

  const confirmPasswordResetWithOobCode = async (oobCode, newPassword) => {
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      return { isSuccess: true };
    } catch (err) {
      return { isSuccess: false, errorCode: err.code, errorMessage: err.message };
    }
  }

  const sendEmailVerificationFirebase = async (user) => {
    try {
      const targetUser = auth.currentUser || user;
      if (!targetUser) {
        throw new Error('email_verification_no_user');
      }
      await sendEmailVerification(targetUser);
      return {
        isSuccess: true,
        message: "Email onay e-postası başarıyla gönderildi"
      };
    } catch (err) {
      console.log('[useFirebase] Firebase email onay hatası:', err);
      return {
        isSuccess: false,
        errorCode: err.code,
        errorMessage: err.message
      };
    }
  }

  //!messaging

  const getFCMToken = async () => {
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        return '';
      }
      if (Notification.permission !== 'granted') {
        return '';
      }
      if (!vapidKey) {
        console.warn('[FCM] getFCMToken: VAPID key yok (REACT_APP_FIREBASE_VAPID_KEY .env\'de tanımlı mı?)');
        return '';
      }
      const token = await getToken(messaging, { vapidKey });
      if (token) {
        dispatch(setNotificationToken(token));
        dispatch(setNotificationInitializer(true));
        return token;
      }
      return '';
    } catch (err) {
      console.error('[FCM] getFCMToken hata:', err);
      return '';
    }
  };
  // bu kisimda gerekirse dbye call atip device tokeni eklemek lazim
  const requestPermission = async () => {
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        return '';
      }
      if (!vapidKey) {
        console.warn('[FCM] requestPermission: VAPID key yok. .env dosyasına REACT_APP_FIREBASE_VAPID_KEY ekleyin.');
        return '';
      }
      const current = Notification.permission;
      const permission = current === 'granted' ? 'granted' : await Notification.requestPermission();
      if (permission !== 'granted') {
        return '';
      }
      const token = await getToken(messaging, { vapidKey });
      if (token) {
        dispatch(setNotificationToken(token));
        dispatch(setNotificationInitializer(true));
        return token;
      }
      return token || '';
    } catch (err) {
      console.error('[FCM] requestPermission hata:', err);
      return '';
    }
  };

  const disableNotifications = async () => {
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        dispatch(setNotificationToken(''));
        dispatch(setNotificationInitializer(false));
        return true;
      }
      try {
        await deleteToken(messaging);
      } catch (_) {
        // If deleteToken fails, still clear local state so UI reflects "off"
      }
      dispatch(setNotificationToken(''));
      dispatch(setNotificationInitializer(false));
      return true;
    } catch (_) {
      return false;
    }
  };


  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onSwMessage = (event) => {
      if (event.data?.type === 'NEW_BACKGROUND_NOTIFICATION') {
        const payload = event.data.payload;
        const item = handleIncomingFcm(payload);
        console.log('[FCM] Arka plan mesajı:', {
          messageId: payload?.messageId,
          title: item.title,
          body: item.body,
          data: item.data,
          payload,
        });
      }
    };
    navigator.serviceWorker.addEventListener('message', onSwMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onSwMessage);
  }, [dispatch]);

  /**
   * Telefon doğrulamasından sonra kayıt oturumu.
   * password verilirse email+password linklenir; doktor kaydında password yok — yalnızca mevcut UID + token.
   */
  const resolveRegisterFirebaseSession = async ({ email, password } = {}) => {
    if (password) {
      return linkEmailPasswordToCurrentUser(email, password);
    }

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return {
          isSuccess: false,
          errorCode: "no-current-user",
          errorMessage: "Mevcut kullanıcı bulunamadı",
        };
      }

      if (email && currentUser.email !== email) {
        try {
          await updateEmailFirebase(currentUser, email);
        } catch (e) {
          // Email güncellenemese de token ile devam (backend email alanını ayrı alır)
          console.warn("[useFirebase] Kayıt email güncelleme atlandı:", e?.code || e?.message);
        }
      }

      try {
        if (currentUser.email && !currentUser.emailVerified) {
          await sendEmailVerification(currentUser);
        }
      } catch (e) {
        console.warn("[useFirebase] Email verification gönderilemedi:", e?.code || e?.message);
      }

      const idToken = await currentUser.getIdToken(true);
      return { isSuccess: true, user: currentUser, idToken };
    } catch (err) {
      return {
        isSuccess: false,
        errorCode: err?.code,
        errorMessage: err?.message,
      };
    }
  };

  useEffect(() => {
      const unsubscribe =
        onMessage(messaging, (payload) => {
          const item = handleIncomingFcm(payload);
          console.log('[FCM] Foreground mesajı:', {
            messageId: payload.messageId,
            title: item.title,
            body: item.body,
            data: item.data,
            payload,
          });
          toast.info(t("new_notification"), { autoClose: 5000 });
        })
      return () => unsubscribe()
  }, [messaging, dispatch, t])

  return {
    register,
    firebaseLogin,
    logout,
    updateEmail,
    getSignature,
    updatePassword,
    hasPasswordProvider,
    changePasswordWithReauth,
    signInWithGoogle,
    signInWithPhoneNumberFirebase,
    registerWithEmailAndPasswordFirebase,
    linkEmailPasswordToCurrentUser,
    resolveRegisterFirebaseSession,
    linkPhoneNumberToCurrentUser,
    confirmLinkedPhoneCode,
    generateRecaptcha,
    deleteUserFirebase,
    getFCMToken,
    requestPermission,
    disableNotifications,
    sendResetPasswordEmail,
    verifyPasswordResetOobCode,
    confirmPasswordResetWithOobCode,
    sendEmailVerificationFirebase,
    // Email verification için gerekli
    auth,
    analytics,
    signInWithEmailAndPassword,
    signOut,
  };
};

export default useFirebase;